// src/pages/AdminTranslations.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, isAdmin } from '../services/authService';
import LoadingState from '../components/common/LoadingState';
import { SUPPORTED_LANGUAGES } from '../services/translationService';
import axios from 'axios';
import { API_CONFIG } from '../components/config/api';
import { ArrowLeftIcon, PlusIcon, CheckIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface Translation {
  id: number;
  key: string;
  translations: Record<string, string>;
}

const AdminTranslations: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [filteredTranslations, setFilteredTranslations] = useState<Translation[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isUserAdmin, setIsUserAdmin] = useState<boolean>(false);
  const [editingTranslation, setEditingTranslation] = useState<{
    id: number;
    key: string;
    language: string;
    value: string;
  } | null>(null);
  const [addingNewKey, setAddingNewKey] = useState<boolean>(false);
  const [newKey, setNewKey] = useState<{
    key: string;
    translations: Record<string, string>;
  }>({
    key: '',
    translations: SUPPORTED_LANGUAGES.reduce((acc, lang) => {
      acc[lang.code] = '';
      return acc;
    }, {} as Record<string, string>)
  });
  
  const pageSize = 25;

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        if (!isAuthenticated()) {
          navigate('/login', { state: { returnPath: '/admin/translations' } });
          return;
        }

        const hasAdminRights = isAdmin();
        setIsUserAdmin(hasAdminRights);

        if (!hasAdminRights) {
          setError('You do not have administrator rights');
        } else {
          fetchTranslations();
        }
      } catch (err) {
        console.error('Error checking admin access:', err);
        setError('An error occurred while checking your access rights');
      }
    };

    checkAdminAccess();
  }, [navigate]);

  useEffect(() => {
    // Filter translations when search term changes
    if (searchTerm) {
      const filtered = translations.filter(t => 
        t.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.translations[selectedLanguage] && 
          t.translations[selectedLanguage].toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredTranslations(filtered);
      setTotalPages(Math.ceil(filtered.length / pageSize));
    } else {
      setFilteredTranslations(translations);
      setTotalPages(Math.ceil(translations.length / pageSize));
    }
    setPage(1);
  }, [searchTerm, translations, selectedLanguage]);

  const fetchTranslations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_CONFIG.BASE_URL}/admin/translations`);
      
      if (response.data.status === 'success') {
        setTranslations(response.data.data.translations);
        setFilteredTranslations(response.data.data.translations);
        setTotalPages(Math.ceil(response.data.data.translations.length / pageSize));
      } else {
        throw new Error(response.data.message || 'Failed to fetch translations');
      }
    } catch (err: any) {
      console.error('Error loading translations:', err);
      setError(err.message || 'Failed to load translations');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTranslation = async () => {
    if (!editingTranslation) return;
    
    try {
      const response = await axios.put(
        `${API_CONFIG.BASE_URL}/admin/translations/${editingTranslation.id}`, 
        {
          language: editingTranslation.language,
          translation: editingTranslation.value
        }
      );
      
      if (response.data.status === 'success') {
        // Update local state
        const updatedTranslations = translations.map(t => {
          if (t.id === editingTranslation.id) {
            return {
              ...t,
              translations: {
                ...t.translations,
                [editingTranslation.language]: editingTranslation.value
              }
            };
          }
          return t;
        });
        
        setTranslations(updatedTranslations);
        setEditingTranslation(null);
      } else {
        throw new Error(response.data.message || 'Failed to update translation');
      }
    } catch (err: any) {
      console.error('Error updating translation:', err);
      setError(err.message || 'Failed to update translation');
    }
  };

  const handleAddKey = async () => {
    if (!newKey.key.trim()) {
      setError('Key is required');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/admin/translations`,
        newKey
      );
      
      if (response.data.status === 'success') {
        // Add to local state
        setTranslations([...translations, response.data.data]);
        
        // Reset form
        setNewKey({
          key: '',
          translations: SUPPORTED_LANGUAGES.reduce((acc, lang) => {
            acc[lang.code] = '';
            return acc;
          }, {} as Record<string, string>)
        });
        
        setAddingNewKey(false);
      } else {
        throw new Error(response.data.message || 'Failed to add translation key');
      }
    } catch (err: any) {
      console.error('Error adding translation key:', err);
      setError(err.message || 'Failed to add translation key');
    }
  };

  const handleDeleteTranslation = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this translation key? This will remove all translations for this key in all languages.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API_CONFIG.BASE_URL}/admin/translations/${id}`);
      
      if (response.data.status === 'success') {
        // Remove from local state
        setTranslations(translations.filter(t => t.id !== id));
      } else {
        throw new Error(response.data.message || 'Failed to delete translation');
      }
    } catch (err: any) {
      console.error('Error deleting translation:', err);
      setError(err.message || 'Failed to delete translation');
    }
  };

  const handleImportTranslations = async (file: File, language: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);
    
    try {
      setLoading(true);
      
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/admin/translations/import`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.status === 'success') {
        // Refresh translations list
        fetchTranslations();
      } else {
        throw new Error(response.data.message || 'Failed to import translations');
      }
    } catch (err: any) {
      console.error('Error importing translations:', err);
      setError(err.message || 'Failed to import translations');
    } finally {
      setLoading(false);
    }
  };

  const handleExportTranslations = async (language: string) => {
    try {
      setLoading(true);
      
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/admin/translations/export?language=${language}`,
        {
          responseType: 'blob'
        }
      );
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `translations_${language}.js`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (err: any) {
      console.error('Error exporting translations:', err);
      setError(err.message || 'Failed to export translations');
    } finally {
      setLoading(false);
    }
  };

  const getVisibleTranslations = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredTranslations.length);
    return filteredTranslations.slice(startIndex, endIndex);
  };

  if (!isUserAdmin && !loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-destiny-red mb-4">Access Denied</h2>
        <p className="text-gray-300 mb-4">You do not have administrator rights to access this page.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-destiny-gold">Manage Translations</h1>
        <button 
          onClick={() => navigate('/admin')} 
          className="inline-flex items-center text-destiny-blue hover:text-destiny-gold"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back to Admin
        </button>
      </div>
      
      {loading && <LoadingState message="Loading translations..." />}
      
      {error && (
        <div className="bg-destiny-red bg-opacity-20 border border-destiny-red rounded-lg p-4 mb-6">
          <h3 className="text-destiny-red font-medium mb-2">Error</h3>
          <p className="text-gray-300">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="mt-2 text-white bg-destiny-red bg-opacity-30 hover:bg-opacity-50 px-3 py-1 rounded text-sm"
          >
            Dismiss
          </button>
        </div>
      )}
      
      {!loading && (
        <>
          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
              {/* Language Filter */}
              <div>
                <label htmlFor="language-filter" className="block text-sm font-medium text-gray-400 mb-1">
                  Language
                </label>
                <select
                  id="language-filter"
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="bg-gray-700 text-white rounded py-2 px-3 focus:outline-none focus:ring-2 focus:ring-destiny-gold"
                >
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Search */}
              <div className="flex-1">
                <label htmlFor="search" className="block text-sm font-medium text-gray-400 mb-1">
                  Search
                </label>
                <div className="relative">
                  <input
                    id="search"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by key or translation"
                    className="w-full bg-gray-700 text-white rounded py-2 pl-3 pr-10 focus:outline-none focus:ring-2 focus:ring-destiny-gold"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div>
                <label className="invisible block text-sm font-medium text-gray-400 mb-1">
                  Actions
                </label>
                <button
                  onClick={() => setAddingNewKey(true)}
                  className="bg-destiny-blue hover:bg-opacity-90 text-white py-2 px-4 rounded inline-flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-1" />
                  Add Translation Key
                </button>
              </div>
            </div>
          </div>
          
          {/* Add New Key Form */}
          {addingNewKey && (
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Add New Translation Key</h2>
                <button 
                  onClick={() => setAddingNewKey(false)} 
                  className="text-gray-400 hover:text-white"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Key
                  </label>
                  <input
                    type="text"
                    value={newKey.key}
                    onChange={(e) => setNewKey({...newKey, key: e.target.value})}
                    placeholder="e.g. nav.dashboard"
                    className="w-full bg-gray-700 text-white rounded py-2 px-3 focus:outline-none focus:ring-2 focus:ring-destiny-gold"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <div key={lang.code}>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        {lang.name}
                      </label>
                      <input
                        type="text"
                        value={newKey.translations[lang.code] || ''}
                        onChange={(e) => setNewKey({
                          ...newKey, 
                          translations: {
                            ...newKey.translations,
                            [lang.code]: e.target.value
                          }
                        })}
                        placeholder={`Translation in ${lang.name}`}
                        className="w-full bg-gray-700 text-white rounded py-2 px-3 focus:outline-none focus:ring-2 focus:ring-destiny-gold"
                      />
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setAddingNewKey(false)}
                    className="bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddKey}
                    className="bg-destiny-blue hover:bg-opacity-90 text-white py-2 px-4 rounded"
                  >
                    Add Key
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Import/Export Tools */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Import/Export Translations</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-destiny-gold text-lg mb-2">Import Translations</h3>
                <p className="text-gray-300 mb-4">
                  Upload a JavaScript translation file to import translations for a specific language.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Language
                    </label>
                    <select
                      className="bg-gray-700 text-white rounded py-2 px-3 focus:outline-none focus:ring-2 focus:ring-destiny-gold w-full"
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      value={selectedLanguage}
                    >
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      File
                    </label>
                    <input
                      type="file"
                      accept=".js,.ts"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleImportTranslations(e.target.files[0], selectedLanguage);
                        }
                      }}
                      className="block w-full text-sm text-gray-300
                        file:mr-4 file:py-2 file:px-4
                        file:rounded file:border-0
                        file:text-sm file:font-medium
                        file:bg-destiny-blue file:text-white
                        hover:file:bg-opacity-90"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-destiny-gold text-lg mb-2">Export Translations</h3>
                <p className="text-gray-300 mb-4">
                  Export translations for a specific language as a JavaScript file.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Language
                  </label>
                  <div className="flex space-x-4">
                    <select
                      className="bg-gray-700 text-white rounded py-2 px-3 focus:outline-none focus:ring-2 focus:ring-destiny-gold w-full"
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      value={selectedLanguage}
                    >
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                    
                    <button
                      onClick={() => handleExportTranslations(selectedLanguage)}
                      className="bg-destiny-green hover:bg-opacity-90 text-white py-2 px-4 rounded whitespace-nowrap"
                    >
                      Export
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Translations Table */}
          <div className="bg-gray-800 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Key
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Translation ({SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.name})
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {getVisibleTranslations().length > 0 ? (
                    getVisibleTranslations().map((t) => (
                      <tr key={t.id} className="hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                          {t.key}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300">
                          {editingTranslation && editingTranslation.id === t.id && editingTranslation.language === selectedLanguage ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={editingTranslation.value}
                                onChange={(e) => setEditingTranslation({
                                  ...editingTranslation,
                                  value: e.target.value
                                })}
                                className="bg-gray-700 text-white rounded py-2 px-3 focus:outline-none focus:ring-2 focus:ring-destiny-gold w-full"
                              />
                              <button 
                                onClick={handleSaveTranslation}
                                className="text-destiny-green hover:text-destiny-gold"
                              >
                                <CheckIcon className="h-5 w-5" />
                              </button>
                              <button 
                                onClick={() => setEditingTranslation(null)}
                                className="text-destiny-red hover:text-red-500"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ) : (
                            <div 
                              className="cursor-pointer hover:bg-gray-700 p-2 rounded"
                              onClick={() => setEditingTranslation({
                                id: t.id,
                                key: t.key,
                                language: selectedLanguage,
                                value: t.translations[selectedLanguage] || ''
                              })}
                            >
                              {t.translations[selectedLanguage] || (
                                <span className="text-gray-500 italic">No translation</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <button
                            onClick={() => handleDeleteTranslation(t.id)}
                            className="text-destiny-red hover:text-red-500 ml-2"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-6 py-4 text-center text-gray-400">
                        No translations found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination */}
          {filteredTranslations.length > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-800 rounded-lg">
              <div className="flex-1 flex justify-between">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md text-gray-200 bg-gray-700 ${
                    page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-600'
                  }`}
                >
                  Previous
                </button>
                <div className="hidden md:flex">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 mx-1 border ${
                          page === pageNum
                            ? 'border-destiny-gold bg-gray-700 text-destiny-gold'
                            : 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'
                        } text-sm font-medium rounded-md`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <div className="flex md:hidden">
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-700 bg-gray-800 text-sm font-medium rounded-md text-gray-300">
                    Page {page} of {totalPages}
                  </span>
                </div>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md text-gray-200 bg-gray-700 ${
                    page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-600'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminTranslations;